import { GenericError } from '@starsoft/common/models';
import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import {
  UsePhoneNumbersLookupProps,
  UserPhoneNumbersLookupQueryKey,
} from './props';
import { UserPhoneNumbersQueryKey } from '../../queryKey';
import { fetchPhoneNumbersLookup } from './service';
import { UserPhoneNumberLookupOutputDto } from '@starsoft/common/dto';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to lookup phone numbers based on country code and number
 *
 * This hook handles looking up phone numbers, managing the query state using React Query.
 * It cleans the phone number input by removing special characters and spaces before lookup.
 * The data is considered stale after 5 seconds.
 *
 * @param {UsePhoneNumbersLookupProps} props - The props object
 * @param {string} props.countryCode - The country code to lookup phone numbers for
 * @param {string} props.phoneNumber - The phone number to lookup
 * @param {boolean} [props.enabled=true] - Whether the query should be enabled
 *
 * @returns {Object} An object containing:
 *   - error: Any error that occurred during the lookup
 *   - refetch: Function to manually trigger a refetch
 *   - isSuccess: Boolean indicating if lookup was successful
 *   - fetchStatus: Current status of the fetch operation
 *   - phoneNumbersLookup: The lookup results data
 *   - isPending: Boolean indicating if lookup is in progress (excluding idle state)
 */
export function usePhoneNumbersLookup({
  countryCode,
  phoneNumber,
  enabled = true,
}: UsePhoneNumbersLookupProps) {
  const axiosInstance: AxiosInstance = useAxiosInstance();
  const queryKey: UserPhoneNumbersLookupQueryKey = [
    UserPhoneNumbersQueryKey.Lookup,
    {
      countryCode,
      phoneNumber: phoneNumber?.replace(/[()\-\s]/g, '')?.replace(' ', ''),
    },
  ];

  const { data, isPending, error, refetch, fetchStatus, isSuccess } = useQuery<
    UserPhoneNumberLookupOutputDto,
    AxiosError<GenericError>,
    UserPhoneNumberLookupOutputDto,
    UserPhoneNumbersLookupQueryKey
  >({
    queryKey,
    queryFn: queryContext =>
      fetchPhoneNumbersLookup(queryContext, axiosInstance),
    enabled: enabled && !!countryCode && !!phoneNumber,
    staleTime: 5 * 1000,
  });

  return {
    error,
    refetch,
    isSuccess,
    fetchStatus,
    phoneNumbersLookup: data,
    isPending: isPending && fetchStatus !== 'idle',
  };
}
